/* biome-ignore-all lint/complexity/noExcessiveCognitiveComplexity: Grid setup requires complex configuration */
import { AG_GRID_LOCALE_UA } from '@ag-grid-community/locale'
import {
  CellApiModule,
  type CellKeyDownEvent,
  type CellRange,
  type CellSelectionChangedEvent,
  CellStyleModule,
  CheckboxEditorModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  type ColDef,
  type ColGroupDef,
  ColumnApiModule,
  ColumnAutoSizeModule,
  CsvExportModule,
  CustomEditorModule,
  createGrid,
  DragAndDropModule,
  EventApiModule,
  enableDevValidations,
  type GetContextMenuItems,
  type GetContextMenuItemsParams,
  type GetDetailRowDataParams,
  type GridApi,
  type GridOptions,
  HighlightChangesModule,
  type IServerSideDatasource,
  LargeTextEditorModule,
  LocaleModule,
  type MenuItemDef,
  ModuleRegistry,
  NumberEditorModule,
  PinnedRowModule,
  RenderApiModule,
  RowApiModule,
  type RowClassParams,
  RowDragModule,
  RowSelectionModule,
  RowStyleModule,
  ScrollApiModule,
  SelectEditorModule,
  type SelectionChangedEvent,
  TextEditorModule,
  TooltipModule,
  UndoRedoEditModule
} from 'ag-grid-community'
import {
  CellSelectionModule,
  ClipboardModule,
  ContextMenuModule,
  ExcelExportModule,
  LicenseManager,
  MasterDetailModule,
  RichSelectModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule
} from 'ag-grid-enterprise'
import type { ViewHook } from 'phoenix_live_view'
import { isObjectType, isString } from 'remeda'
import {
  FORMATTER_TYPES,
  GRID_DEFAULTS,
  GRID_MODEL_TYPES,
  PHOENIX_EVENTS
} from './constants'
import { createCellValueChangedHandler } from './events/cell-value-change'
import { createColumnUpdateHandler } from './events/column-updates'
import {
  configureGridReadonly,
  createPhoenixEventHandlers
} from './events/phoenix-handlers'
import { createRowDragEndHandler } from './events/row-drag'
import { createGridSorting, type GridSorting } from './events/sorting'
import {
  configureColumnSorting,
  type ProcessColumnsFunction,
  pinnedBottomData,
  processColumns,
  resolveCellFormatter
} from './utils/column-processors'
import { createIcon } from './utils/dom'
import type {
  GridHTMLElement,
  RowMenuConfig,
  RowMenuItem
} from './utils/grid-config'
import {
  createClipboardConfig,
  createExportConfig,
  createGridContext,
  createTooltipConfig,
  gridTheme
} from './utils/grid-config'
import { getGridIcons } from './utils/icons-config'
import { createInitialGridReveal } from './utils/initial-reveal'
import {
  beginGridInitialization,
  destroyGridAfterRemoval
} from './utils/navigation'
import type { GridRenderContext } from './utils/render-timing'
import {
  logGridRenderTiming,
  measureGridRender,
  reportGridRender
} from './utils/render-timing'
import {
  getGridExportMenuMode,
  hasSelectedRowsForExport
} from './utils/row-selection-export'
import { createGridRuntime, type GridRuntime } from './utils/runtime'
import {
  createServerSideDatasource,
  type DatasourceWithCache,
  seedServerSideFirstPage
} from './utils/server-side-datasource'

if (process.env.AG_GRID_LICENSE_KEY) {
  LicenseManager.setLicenseKey(process.env.AG_GRID_LICENSE_KEY)
}

// Translation constants for context menu items
const CONTEXT_MENU_TRANSLATIONS = {
  exportDisabled: {
    en: 'Clear selected rows before exporting',
    uk: 'Очистьте вибір рядків перед експортом'
  },
  openInNewTab: {
    en: 'Open in New Tab',
    uk: 'Відкрити в новій вкладці'
  }
} as const

if (process.env.NODE_ENV !== 'production') {
  enableDevValidations()
}

ModuleRegistry.registerModules([
  ColumnAutoSizeModule,
  PinnedRowModule,
  RowStyleModule,
  RowDragModule,
  RowSelectionModule,
  CellStyleModule,
  HighlightChangesModule,
  TooltipModule,
  TextEditorModule,
  LargeTextEditorModule,
  SelectEditorModule,
  NumberEditorModule,
  CheckboxEditorModule,
  CustomEditorModule,
  UndoRedoEditModule,
  LocaleModule,
  CsvExportModule,
  DragAndDropModule,
  ColumnApiModule,
  RowApiModule,
  CellApiModule,
  ScrollApiModule,
  RenderApiModule,
  EventApiModule,
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  CellSelectionModule,
  RichSelectModule,
  ContextMenuModule,
  ExcelExportModule,
  ClipboardModule,
  MasterDetailModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule
])

// Build event payload structure
type BuildEventPayload = {
  readonly id: string
}

// Enhanced Phoenix hook type for grid functionality
type GridViewHook = ViewHook & {
  grid: GridApi
  _gridSorting?: GridSorting
  _gridRuntime?: GridRuntime
  _gridContext?: {
    readonly movableRows: boolean
    readonly readonly: boolean
  }
  _initFrame?: number
  _finishInitialization?: () => void
  _datasourceCleanup?: () => void
  _invalidateDatasourceCache?: () => void
  _retireDatasourceCache?: () => void
  _resumeDatasourceRequests?: DatasourceWithCache['resumeRequests']
  _cellValueChangedCleanup?: () => void
  /** Resets the SSRM datasource page-1 cache. Set during mount when SSRM is active. */
  _resetDatasourceCache?: (
    data: readonly Record<string, unknown>[],
    meta?: {
      readonly current_page: number
      readonly next_page: number | null
      readonly page_size: number
      readonly total_count: number
      readonly total_pages: number
    }
  ) => void
}

type GridRow = Record<string, unknown>

const DETAIL_GRID_HEADER_HEIGHT = 32
const DETAIL_ROW_VERTICAL_PADDING = 44

const isGridRow = (value: unknown): value is GridRow =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const detailRowsFrom = (
  row: GridRow | undefined,
  detailsField: string
): GridRow[] => {
  const details = row?.[detailsField]

  return Array.isArray(details) ? details.filter(isGridRow) : []
}

/** Keep the cell formatter inside the native expand/collapse renderer. */
const withMasterDetailColumn = (
  columns: readonly (ColDef | ColGroupDef)[],
  masterColumn: string
): readonly (ColDef | ColGroupDef)[] =>
  columns.map((column) => {
    if ('children' in column) {
      return {
        ...column,
        children: withMasterDetailColumn(column.children, masterColumn)
      }
    }

    if (column.field !== masterColumn) {
      return column
    }

    return {
      ...column,
      cellRenderer: 'agGroupCellRenderer',
      cellRendererSelector: undefined,
      cellRendererParams: {
        suppressCount: true,
        innerRendererSelector: column.cellRendererSelector,
        innerRendererParams: column.cellRendererParams
      }
    }
  })

/** Apply the same master-detail configuration at creation and on server updates. */
const createGridColumnProcessor =
  (
    masterColumn: string | undefined,
    sortingBound: boolean
  ): ProcessColumnsFunction =>
  (columns, movableRows, readonly) => {
    const processedColumns = configureColumnSorting(
      processColumns(columns, movableRows, readonly),
      sortingBound
    )

    return masterColumn === undefined
      ? processedColumns
      : withMasterDetailColumn(processedColumns, masterColumn)
  }

/**
 * Grid Initialization Helpers
 */

/**
 * Count leaf columns from a column definitions array.
 * A leaf column is a ColDef (has `field` or is not a group); ColGroupDef has `children`.
 * This handles potential mixed arrays robustly — even though processColumns currently
 * returns only leaf ColDefs.
 */
export const countLeafColumns = (
  cols: readonly (ColDef | ColGroupDef)[]
): number =>
  cols.reduce<number>((total, col) => {
    if ('children' in col) {
      return (
        total +
        countLeafColumns(col.children as readonly (ColDef | ColGroupDef)[])
      )
    }

    return total + 1
  }, 0)

/** Check if progressive load event is valid */
const isValidProgressiveLoadEvent = (event: unknown): event is string =>
  isString(event) && event.trim().length > 0

/** Warn if async grid has invalid configuration */
const warnIfInvalidAsyncConfig = (
  isAsync: boolean,
  hasDatasource: boolean,
  gridId: string
): void => {
  if (isAsync && !hasDatasource) {
    console.warn(
      'Grid configured as async but no valid onProgressiveLoad event provided for grid:',
      gridId
    )
  }
}

/** Create row class getter for styling */
const createRowClassGetter =
  () =>
  (params: RowClassParams): string | undefined => {
    if (params.node.rowPinned === 'bottom') {
      return '!bg-slate-2'
    }
    return undefined
  }

/** Extract selected row IDs from grid rows */
const extractSelectedRowIds = (
  rows: readonly Record<string, unknown>[]
): readonly (string | number)[] =>
  rows.map((row) => (row.id as string | number) ?? row)

/** Send Phoenix event with proper target routing */
const sendPhoenixEvent = (
  hook: ViewHook,
  target: string | null | undefined,
  eventName: string,
  payload: Record<string, unknown>
): void => {
  if (target) {
    hook.pushEventTo(target, eventName, payload)
  } else {
    hook.pushEvent(eventName, payload)
  }
}

/**
 * Transform Elixir row menu item to AG Grid MenuItemDef
 *
 * Converts row menu configuration from Elixir format to AG Grid's MenuItemDef format,
 * handling icons, actions, and disabled states. When action is triggered, sends
 * Phoenix event with row data and action identifier.
 */
const transformRowMenuItem = (
  item: RowMenuItem,
  hook: ViewHook,
  target: string | null | undefined,
  eventName: string | undefined,
  rowData: Record<string, unknown>
): MenuItemDef | 'separator' => {
  // Handle separator items
  if (item.separator) {
    return 'separator'
  }

  return {
    name: item.label,
    // Convert Facade icon to HTML string for AG Grid
    icon: item.icon
      ? `<div style="display:flex;align-items:center;">${createIcon(item.icon).outerHTML}</div>`
      : undefined,
    disabled: item.disabled ?? false,
    action: () => {
      if (!eventName) {
        console.warn(
          '[Grid Context Menu] No onRowMenu event handler configured for grid'
        )
        return
      }

      // Extract row ID from row data for backend pattern matching
      const rowId = rowData.id as string | number | undefined

      if (!rowId) {
        console.error('[Grid Context Menu] Row data missing ID:', rowData)
        return
      }

      const payload = {
        action: item.action,
        row_id: String(rowId)
      }

      sendPhoenixEvent(hook, target, eventName, payload)
    }
  }
}

/**
 * Create row menu items from configuration
 *
 * Transforms list of menu items from grid configuration into AG Grid MenuItemDef
 * objects. Returns empty array if no menu configuration or row data available.
 */
const createRowMenuItems = (
  params: GetContextMenuItemsParams,
  rowMenu: RowMenuConfig | undefined,
  hook: ViewHook,
  target: string | null | undefined,
  eventName: string | undefined
): (MenuItemDef | 'separator')[] => {
  if (!rowMenu?.items || rowMenu.items.length === 0) {
    return []
  }

  const rowData = params.node?.data as Record<string, unknown> | undefined

  if (!rowData) {
    console.warn('[Grid Context Menu] No row data available for context menu')
    return []
  }

  const menuItems = rowMenu.items.map((item) =>
    transformRowMenuItem(item, hook, target, eventName, rowData)
  )

  return menuItems
}

/** Check if selection source indicates select all operation */
const isSelectAllSource = (source?: string): boolean => {
  if (!source) {
    return false
  }
  return (
    source.includes('SelectAll') ||
    source === 'keyboardSelectAll' ||
    source === 'uiSelectAll'
  )
}

/** Check if grid is using Server-Side Row Model */
const isServerSideRowModel = (api: GridApi): boolean => {
  const rowModelType = api.getGridOption('rowModelType')
  return rowModelType === 'serverSide'
}

/**
 * SSRM selection state structure
 */
type SSRMSelectionState = {
  readonly selectAll: boolean
  readonly toggledNodes: readonly string[]
}

/**
 * Get SSRM selection state with toggled nodes
 * Returns selectAll flag and array of toggled (excluded) node IDs
 */
const getSSRMSelectionState = (api: GridApi): SSRMSelectionState => {
  try {
    const selectionState = api.getServerSideSelectionState()
    // Type guard: check if this is IServerSideSelectionState (has selectAll property)
    // vs IServerSideGroupSelectionState (grouped rows, no selectAll)
    if (
      selectionState &&
      isObjectType(selectionState) &&
      'selectAll' in selectionState
    ) {
      return {
        selectAll: selectionState.selectAll === true,
        toggledNodes: (selectionState.toggledNodes as readonly string[]) ?? []
      }
    }
    return { selectAll: false, toggledNodes: [] }
  } catch (_error: unknown) {
    // Fallback if method doesn't exist - intentionally ignored
    return { selectAll: false, toggledNodes: [] }
  }
}

/**
 * Get selected row IDs for client-side grids
 * Returns array of selected row IDs
 */
const getClientSideSelection = (api: GridApi): readonly (string | number)[] => {
  const selectedRows = api.getSelectedRows()
  return extractSelectedRowIds(selectedRows)
}

/**
 * Create selection changed handler that translates AG Grid events
 * to individual row selection events for backend compatibility
 */
const createSelectionChangedHandler = (
  hook: ViewHook,
  _gridId: string,
  target?: string | null
) => {
  // Track previous selection state (per-grid instance)
  let previousSelectedIds = new Set<string | number>()
  // For SSRM: track previous toggled nodes (deselected rows when in select-all mode)
  let previousToggledNodes = new Set<string>()
  // Track if last operation was a select-all
  let wasSelectAll = false

  return (event: SelectionChangedEvent): void => {
    const { source, api } = event
    const isSSRM = isServerSideRowModel(api)

    // Defer selection check to ensure AG Grid's internal state is updated
    setTimeout(() => {
      if (isSSRM) {
        // Handle SSRM selection
        const ssrmState = getSSRMSelectionState(api)
        const currentToggledNodes = new Set(ssrmState.toggledNodes)

        // Case 1: Transition to select-all (no exclusions)
        if (
          ssrmState.selectAll &&
          currentToggledNodes.size === 0 &&
          !wasSelectAll
        ) {
          sendPhoenixEvent(hook, target, 'row_selected_all', {})
          previousToggledNodes = new Set()
          wasSelectAll = true
          return
        }

        // Case 2: Transition from select-all to nothing
        if (
          !ssrmState.selectAll &&
          currentToggledNodes.size === 0 &&
          wasSelectAll
        ) {
          sendPhoenixEvent(hook, target, 'row_deselected_all', {})
          previousToggledNodes = new Set()
          wasSelectAll = false
          return
        }

        // Case 3: In select-all mode, track toggled (deselected) nodes
        if (ssrmState.selectAll) {
          // Find newly toggled (deselected) nodes
          const newlyDeselected = Array.from(currentToggledNodes).filter(
            (id) => !previousToggledNodes.has(id)
          )
          // Find re-selected nodes (removed from toggled list)
          const reselected = Array.from(previousToggledNodes).filter(
            (id) => !currentToggledNodes.has(id)
          )

          for (const id of newlyDeselected) {
            sendPhoenixEvent(hook, target, 'row_deselected', { rowId: id })
          }
          for (const id of reselected) {
            sendPhoenixEvent(hook, target, 'row_selected', { rowId: id })
          }

          previousToggledNodes = currentToggledNodes
          wasSelectAll = true
          return
        }

        // Case 4: Not in select-all mode - use toggledNodes as selected IDs
        if (!ssrmState.selectAll) {
          const added = Array.from(currentToggledNodes).filter(
            (id) => !previousToggledNodes.has(id)
          )
          const removed = Array.from(previousToggledNodes).filter(
            (id) => !currentToggledNodes.has(id)
          )

          for (const id of added) {
            sendPhoenixEvent(hook, target, 'row_selected', { rowId: id })
          }
          for (const id of removed) {
            sendPhoenixEvent(hook, target, 'row_deselected', { rowId: id })
          }

          previousToggledNodes = currentToggledNodes
          wasSelectAll = false
          return
        }

        // Reset state if nothing selected
        previousToggledNodes = new Set()
        wasSelectAll = ssrmState.selectAll
      } else {
        // Handle client-side selection
        const currentSelectedIds = new Set(getClientSideSelection(api))
        const previousCount = previousSelectedIds.size
        const currentCount = currentSelectedIds.size

        // Check for select-all
        if (
          isSelectAllSource(source) &&
          previousCount === 0 &&
          currentCount > 0
        ) {
          sendPhoenixEvent(hook, target, 'row_selected_all', {})
          previousSelectedIds = currentSelectedIds
          wasSelectAll = true
          return
        }

        // Check for deselect-all
        if (currentCount === 0 && previousCount > 0) {
          sendPhoenixEvent(hook, target, 'row_deselected_all', {})
          previousSelectedIds = new Set()
          wasSelectAll = false
          return
        }

        // Individual selection changes
        const added = Array.from(currentSelectedIds).filter(
          (id) => !previousSelectedIds.has(id)
        )
        const removed = Array.from(previousSelectedIds).filter(
          (id) => !currentSelectedIds.has(id)
        )

        for (const id of added) {
          sendPhoenixEvent(hook, target, 'row_selected', { rowId: id })
        }
        for (const id of removed) {
          sendPhoenixEvent(hook, target, 'row_deselected', { rowId: id })
        }

        previousSelectedIds = currentSelectedIds
        wasSelectAll = false
      }
    }, 0)
  }
}

/** Send build event to backend */
const sendBuildEvent = (
  hook: ViewHook,
  eventName: string,
  gridId: string,
  target?: string | null
): void => {
  const payload: BuildEventPayload = { id: gridId }

  if (target) {
    hook.pushEventTo(target, eventName, payload)
  } else {
    hook.pushEvent(eventName, payload)
  }
}

/** Create grid ready handler */
const createGridReadyHandler =
  (
    hook: ViewHook,
    gridId: string,
    onBuildEvent?: string | null,
    target?: string | null
  ) =>
  (): void => {
    // Send build event if configured
    if (onBuildEvent) {
      try {
        sendBuildEvent(hook, onBuildEvent, gridId, target)
      } catch (_error: unknown) {
        // Ignore build event dispatch errors to prevent grid initialization failure
      }
    }
  }

/** Register Phoenix event handlers */
const registerPhoenixHandlers = (
  hook: GridViewHook,
  handlers: Record<string, (payload: unknown) => void>
): void => {
  for (const [eventName, handler] of Object.entries(handlers)) {
    hook.handleEvent(eventName, handler)
  }
}

/**
 * Extract column IDs from cell ranges
 * Cell ranges contain columns, not individual cells, so we extract unique column IDs
 */
const extractColumnsFromRanges = (cellRanges: CellRange[]): Set<string> => {
  const columnIds = new Set<string>()

  for (const range of cellRanges) {
    const columns = range.columns
    if (columns) {
      for (const column of columns) {
        columnIds.add(column.getColId())
      }
    }
  }

  return columnIds
}

/**
 * Extract row indices from cell ranges
 * Get min and max row indices to determine which rows to export
 */
const extractRowIndicesFromRanges = (
  cellRanges: CellRange[]
): { minRow: number; maxRow: number } | null => {
  let minRow = Number.POSITIVE_INFINITY
  let maxRow = Number.NEGATIVE_INFINITY

  for (const range of cellRanges) {
    const startRow = range.startRow?.rowIndex
    const endRow = range.endRow?.rowIndex

    if (startRow !== undefined) {
      minRow = Math.min(minRow, startRow)
      maxRow = Math.max(maxRow, startRow)
    }
    if (endRow !== undefined) {
      minRow = Math.min(minRow, endRow)
      maxRow = Math.max(maxRow, endRow)
    }
  }

  if (
    minRow === Number.POSITIVE_INFINITY ||
    maxRow === Number.NEGATIVE_INFINITY
  ) {
    return null
  }

  return { minRow, maxRow }
}

// Shares range boundaries across CSV and Excel, preserving the no-range config.
const createRangeExportConfig = (
  exportConfig: ReturnType<typeof createExportConfig>,
  cellRanges: CellRange[]
): ReturnType<typeof createExportConfig> => {
  const columnIds = extractColumnsFromRanges(cellRanges)
  const rowIndices = extractRowIndicesFromRanges(cellRanges)

  return rowIndices
    ? {
        ...exportConfig,
        columnKeys: Array.from(columnIds),
        shouldRowBeSkipped: ({ node }) =>
          node.rowIndex === null ||
          node.rowIndex < rowIndices.minRow ||
          node.rowIndex > rowIndices.maxRow
      }
    : exportConfig
}

/**
 * Create icon HTML for context menu items
 *
 * @param iconName - Phosphor icon name
 * @returns HTML string for menu icon
 */
const createMenuIcon = (iconName: string): string =>
  `<div style="display:flex;align-items:center;">${createIcon(iconName).outerHTML}</div>`

const getFormatterType = (cellRendererParams: unknown): string | undefined => {
  if (!isObjectType(cellRendererParams) || cellRendererParams === null) {
    return undefined
  }

  const formatterType = Reflect.get(cellRendererParams, 'formatterType')

  return typeof formatterType === 'string' ? formatterType : undefined
}

const readRowFieldValue = (data: unknown, field?: string): unknown =>
  field && isObjectType(data) && data !== null
    ? Reflect.get(data, field)
    : undefined

const resolveEventFormatter = ({
  cellRendererParams,
  data,
  field
}: {
  readonly cellRendererParams: unknown
  readonly data: unknown
  readonly field?: string
}): string | undefined => {
  const formatterType = getFormatterType(cellRendererParams)

  return formatterType
    ? resolveCellFormatter(formatterType, readRowFieldValue(data, field))
    : undefined
}

/**
 * Handle keyboard navigation for redirect cells
 *
 * When Enter key is pressed on a cell with redirect formatter,
 * programmatically clicks the anchor element to trigger navigation.
 * This provides keyboard accessibility for redirect links.
 *
 * @param event - Cell keyboard event from AG Grid
 */
const handleRedirectCellKeyDown = (event: CellKeyDownEvent): void => {
  const keyboardEvent = event.event as KeyboardEvent | undefined

  // Check if Enter key was pressed
  if (
    !keyboardEvent ||
    (keyboardEvent.key !== 'Enter' && keyboardEvent.keyCode !== 13)
  ) {
    return
  }

  const colDef = event.colDef
  const formatterType = resolveEventFormatter({
    cellRendererParams: colDef?.cellRendererParams,
    data: event.data,
    field: colDef?.field
  })

  if (formatterType !== FORMATTER_TYPES.REDIRECT) {
    return
  }

  // Get the focused cell from AG Grid API
  const focusedCell = event.api.getFocusedCell()
  if (!focusedCell) {
    console.warn('[Grid] No focused cell found')
    return
  }

  // Find the cell element using AG Grid's internal structure
  // The event target is usually the grid container, so we need to traverse the DOM
  const cellElement = (keyboardEvent.target as HTMLElement)?.closest('.ag-cell')

  if (!cellElement) {
    console.warn('[Grid] Could not find cell element')
    return
  }

  // Find the anchor element within the cell
  const anchor = cellElement.querySelector(
    'a[data-phx-link]'
  ) as HTMLAnchorElement

  if (!anchor) {
    console.warn('[Grid] Redirect cell missing anchor element', {
      cellElement,
      cellHtml: cellElement.innerHTML
    })
    return
  }

  // Prevent default Enter key behavior (e.g., starting cell edit)
  keyboardEvent.preventDefault()

  // Programmatically click the link to trigger Phoenix navigation
  anchor.click()
}

/**
 * Create context menu items handler with custom row menu and export functionality
 *
 * Merges custom row menu items (from Elixir configuration) with built-in clipboard
 * and export functionality. Custom items appear first, followed by clipboard operations,
 * and then export options (which vary based on cell range selection).
 *
 * Uses AG Grid's Ukrainian locale for translated menu item labels.
 */
const createContextMenuItems =
  (
    exportConfig: ReturnType<typeof createExportConfig>,
    rowMenu: RowMenuConfig | undefined,
    hook: ViewHook,
    target: string | null | undefined,
    onRowMenuEvent: string | undefined
  ): GetContextMenuItems =>
  (params) => {
    const { api } = params
    const cellRanges = api.getCellRanges() ?? []
    const hasCellRangeSelection = cellRanges.length > 0
    const isUkrainian = api.getGridOption('localeText') === AG_GRID_LOCALE_UA
    const exportMenuMode = getGridExportMenuMode({
      hasSelectedRows: hasSelectedRowsForExport(api),
      hasCellRangeSelection
    })

    // Get custom row menu items from configuration
    const customRowItems = createRowMenuItems(
      params,
      rowMenu,
      hook,
      target,
      onRowMenuEvent
    )

    // Detect if clicked cell is a redirect cell and create "Open in New Tab" menu item
    const redirectMenuItems: MenuItemDef[] = []
    const colDef = params.column?.getColDef()
    const cellRendererParams = colDef?.cellRendererParams
    const isRedirectCell =
      resolveEventFormatter({
        cellRendererParams,
        data: params.node?.data,
        field: colDef?.field
      }) === FORMATTER_TYPES.REDIRECT
    const cellValue = params.value as
      | { readonly url?: string; readonly value?: string }
      | undefined

    if (
      isRedirectCell &&
      isObjectType(cellValue) &&
      isString(cellValue.url) &&
      cellValue.url.length > 0
    ) {
      const menuLabel = isUkrainian
        ? CONTEXT_MENU_TRANSLATIONS.openInNewTab.uk
        : CONTEXT_MENU_TRANSLATIONS.openInNewTab.en

      redirectMenuItems.push({
        name: menuLabel,
        icon: createMenuIcon('arrow-square-out'),
        action: () => {
          window.open(cellValue.url as string, '_blank', 'noopener,noreferrer')
        }
      })
    }

    // Get default clipboard menu items (AG Grid translates these automatically)
    const defaultItems = ['copy', 'copyWithHeaders', 'separator'] as const

    // Merge redirect items, custom items, and defaults
    // Redirect items appear first, then custom row items, then defaults
    const baseItems = [
      ...redirectMenuItems,
      ...(redirectMenuItems.length > 0 ? (['separator'] as const) : []),
      ...customRowItems,
      ...(customRowItems.length > 0 ? (['separator'] as const) : []),
      ...defaultItems
    ]

    if (exportMenuMode === 'disabled') {
      const tooltip = isUkrainian
        ? CONTEXT_MENU_TRANSLATIONS.exportDisabled.uk
        : CONTEXT_MENU_TRANSLATIONS.exportDisabled.en

      return [
        ...baseItems,
        {
          name: AG_GRID_LOCALE_UA.export || 'Експорт',
          icon: createMenuIcon('download'),
          disabled: true,
          tooltip
        }
      ]
    }

    // Add export submenu with range-aware functionality and Ukrainian translations
    if (exportMenuMode === 'range') {
      return [
        ...baseItems,
        {
          name: AG_GRID_LOCALE_UA.export || 'Експорт',
          icon: createMenuIcon('download'),
          subMenu: [
            {
              name: AG_GRID_LOCALE_UA.csvExport || 'Експорт CSV',
              icon: createMenuIcon('file-csv'),
              action: () =>
                api.exportDataAsCsv(
                  createRangeExportConfig(exportConfig, cellRanges)
                )
            },
            {
              name: AG_GRID_LOCALE_UA.excelExport || 'Експорт Excel',
              icon: createMenuIcon('file-xls'),
              action: () =>
                api.exportDataAsExcel(
                  createRangeExportConfig(exportConfig, cellRanges)
                )
            }
          ]
        }
      ]
    }

    // No range selection - use default export behavior
    return [...baseItems, 'export'] as const
  }

/** Build comprehensive grid options configuration */
const buildGridOptions = (
  context: ReturnType<typeof createGridContext>,
  columns: readonly (ColDef | ColGroupDef)[],
  pinnedBottomRowData: Record<string, unknown>,
  theme: typeof gridTheme,
  tooltipConfig: ReturnType<typeof createTooltipConfig>,
  exportConfig: ReturnType<typeof createExportConfig>,
  clipboardConfig: ReturnType<typeof createClipboardConfig>,
  handlers: {
    onColumnUpdate: ReturnType<typeof createColumnUpdateHandler>
    onCellValueChanged: ReturnType<typeof createCellValueChangedHandler>
    onRowDragEnd?: ReturnType<typeof createRowDragEndHandler>
    onSelectionChanged?: ReturnType<typeof createSelectionChangedHandler>
    onGridReady: ReturnType<typeof createGridReadyHandler>
  },
  hook: GridViewHook,
  runtime: GridRuntime,
  serverSideDatasource?: IServerSideDatasource,
  renderStartRef?: { value: number }
): GridOptions => {
  // Use server-side mode when async (progressive_load) is configured
  // Use client-side mode otherwise
  const useClientSide = !context.isAsync
  const initialDataLength = context.config.data?.length ?? 0
  const revealInitialGrid = createInitialGridReveal(hook.el, runtime)

  // First-page preload: server-side grid the server gave both data + meta.
  // Tracks whether the one-shot post-seed repaint (onModelUpdated) has run.
  const isSeededPreload =
    !useClientSide &&
    Boolean(context.config.data?.length) &&
    Boolean(context.config.meta)
  let seedRepainted = false
  const hasInitialData = initialDataLength > 0
  const masterDetailConfig = context.config.masterDetail
  const detailColumns = masterDetailConfig
    ? processColumns(masterDetailConfig.columns, false, true)
    : []

  return {
    // Only set rowData for client-side mode (SSRM ignores this, uses datasource instead)
    rowData: useClientSide
      ? ([...(context.config.data ?? [])] as Record<string, unknown>[])
      : undefined,
    // The server placeholder covers initial loading until cells are ready.
    loading: false,
    // Keep native loading cells for later blocks fetched while scrolling.
    // Seed the total count so the initial scrollbar has its final size.
    serverSideInitialRowCount:
      !useClientSide && hasInitialData
        ? (context.config.meta?.total_count ?? initialDataLength)
        : undefined,
    localeText: AG_GRID_LOCALE_UA,
    columnDefs: [...columns] as (ColDef | ColGroupDef)[],
    masterDetail: Boolean(masterDetailConfig),
    isRowMaster: masterDetailConfig
      ? (row: GridRow): boolean =>
          Array.isArray(row[masterDetailConfig.detailsField])
      : undefined,
    detailRowAutoHeight: false,
    getRowHeight: masterDetailConfig
      ? (params): number | undefined => {
          if (!params.node.detail) {
            return undefined
          }

          const detailCount = detailRowsFrom(
            params.node.parent?.data,
            masterDetailConfig.detailsField
          ).length

          return (
            DETAIL_ROW_VERTICAL_PADDING +
            DETAIL_GRID_HEADER_HEIGHT +
            Math.max(detailCount, 1) * GRID_DEFAULTS.ROW_HEIGHT
          )
        }
      : undefined,
    detailCellRendererParams: masterDetailConfig
      ? {
          detailGridOptions: {
            columnDefs: [...detailColumns] as (ColDef | ColGroupDef)[],
            theme,
            domLayout: 'normal',
            rowHeight: GRID_DEFAULTS.ROW_HEIGHT,
            suppressMovableColumns: true,
            cellSelection: true,
            getRowId: (params: { readonly data: GridRow }): string =>
              String(params.data.id)
          },
          getDetailRowData: (
            params: GetDetailRowDataParams<GridRow, GridRow>
          ): void => {
            params.successCallback(
              detailRowsFrom(params.data, masterDetailConfig.detailsField)
            )
          }
        }
      : undefined,
    theme,
    icons: getGridIcons(),
    domLayout: context.domLayout,
    rowHeight: GRID_DEFAULTS.ROW_HEIGHT,
    scrollbarWidth: 8,
    // Use client-side model when initial data is provided for instant rendering
    rowModelType: useClientSide
      ? GRID_MODEL_TYPES.CLIENT_SIDE
      : GRID_MODEL_TYPES.SERVER_SIDE,
    rowSelection: context.selectableRows
      ? { mode: 'multiRow', checkboxes: true, enableClickSelection: false }
      : { mode: 'singleRow', checkboxes: false },
    selectionColumnDef: context.selectableRows
      ? ({
          pinned: 'left',
          suppressNavigable: true,
          suppressFillHandle: true,
          hide: context.readonly
        } as ColDef)
      : undefined,
    cacheBlockSize: useClientSide ? undefined : context.paginationSize,
    // AG Grid default: unlimited cache (no maxBlocksInCache).
    // Block eviction caused spurious "Loading..." rows when scrolling back to evicted blocks.
    // Debounce block loading during rapid scrolling
    blockLoadDebounceMillis: useClientSide
      ? undefined
      : GRID_DEFAULTS.BLOCK_LOAD_DEBOUNCE_MS,
    // Prefetch: render more rows outside viewport to trigger earlier block loading
    rowBuffer: GRID_DEFAULTS.ROW_BUFFER,
    suppressMovableColumns: !context.movableColumns,
    enterNavigatesVerticallyAfterEdit: true,
    rowDragManaged: context.movableRows && useClientSide,
    suppressMoveWhenRowDragging: context.movableRows && !useClientSide,
    // Disable row animations for SSRM list grids to avoid per-row animation
    // work multiplied across hundreds of rows. Client-side report and small
    // modal grids keep animations for visual feedback.
    animateRows: useClientSide,
    cellSelection: true, // Always enable cell selection for clipboard operations
    onCellSelectionChanged: (event: CellSelectionChangedEvent): void => {
      // Only process when the range selection is finished (not during drag)
      if (!event.finished) {
        return
      }

      const cellRanges = event.api.getCellRanges()
      if (!cellRanges?.length) {
        return
      }

      const range = cellRanges[0]
      const columns = range.columns || []

      // Filter out utility columns (checkbox and drag handle)
      const filteredColumns = columns.filter((col) => {
        const colId = col.getColId()
        // Filter out both selection checkbox column and drag handle column
        // Selection column can have multiple possible IDs
        return (
          colId !== 'ag-Grid-AutoColumn' &&
          colId !== '__dragHandle' &&
          !colId.startsWith('ag-Grid-SelectionColumn')
        )
      })

      // If any columns were filtered out, update the range
      if (filteredColumns.length !== columns.length) {
        event.api.clearCellSelection()

        // Only re-add range if there are valid columns left
        if (filteredColumns.length > 0 && range.startRow && range.endRow) {
          event.api.addCellRange({
            rowStartIndex: range.startRow.rowIndex,
            rowEndIndex: range.endRow.rowIndex,
            columns: filteredColumns
          })
        }
      }
    },
    stopEditingWhenCellsLoseFocus: true, // Stop editing when focus moves away
    singleClickEdit: false, // Keep click-start editing on AG Grid's double-click default
    undoRedoCellEditing: true, // Enable undo/redo for cell editing (optional)
    getRowClass: createRowClassGetter(),
    // Prefer the row's real `id` (UUID/PK) so selection events carry a value
    // the backend can use directly — bulk operations pass these IDs into
    // Ecto `where: id in ^ids` queries that expect `:binary_id`.
    //
    // Fall back to `r${__ssrm_row_index__}` only when no real id exists
    // (aggregated/joined views without an `id` column). Without that
    // position-based fallback, `String(params.data)` collapses to
    // "[object Object]" for every such row, AG Grid silently dedupes them,
    // and SSRM thrash-fetches the same blocks forever.
    getRowId: (params) => {
      const realId = params.data?.id
      if (realId !== undefined && realId !== null && realId !== '') {
        return String(realId)
      }
      const ssrmIndex = params.data?.__ssrm_row_index__
      if (typeof ssrmIndex === 'number') {
        return `r${ssrmIndex}`
      }
      return String(params.data)
    },
    pinnedBottomRowData:
      Object.keys(pinnedBottomRowData).length > 0
        ? [pinnedBottomRowData]
        : undefined,
    ...tooltipConfig,
    defaultCsvExportParams: exportConfig,
    defaultExcelExportParams: exportConfig,
    processCellForClipboard: clipboardConfig.processCellForClipboard,
    processCellFromClipboard: clipboardConfig.processCellFromClipboard,
    getContextMenuItems: createContextMenuItems(
      exportConfig,
      context.rowMenu,
      hook,
      context.target,
      context.events.onRowMenu
    ),
    context: {
      hook,
      target: context.target,
      eventName: context.events.onEdited ?? PHOENIX_EVENTS.DEFAULT_EDITED,
      readonly: context.readonly,
      runtime
    },
    onSortChanged: (event) => hook._gridSorting?.onSortChanged(event),
    onColumnResized: handlers.onColumnUpdate,
    onColumnMoved: handlers.onColumnUpdate,
    onCellValueChanged: handlers.onCellValueChanged,
    onCellKeyDown: handleRedirectCellKeyDown,
    onRowDragEnd: handlers.onRowDragEnd,
    onRowDragMove:
      context.movableRows && !useClientSide
        ? (event) => {
            // For server-side unmanaged dragging, show drop indicator with stable positioning
            if (event.overNode) {
              const { rowTop, rowHeight } = event.overNode

              // Calculate relative position within row (0.0 = top, 1.0 = bottom)
              const relativePosition =
                rowTop != null && rowHeight != null
                  ? (event.y - rowTop) / rowHeight
                  : 0.5

              // Use deadzone to prevent jumpy indicator (< 0.4 = above, > 0.6 = below)
              const dropIndicatorPosition =
                relativePosition < 0.4 ? 'above' : 'below'

              event.api.setRowDropPositionIndicator({
                row: event.overNode,
                dropIndicatorPosition
              })
            }
          }
        : undefined,
    onRowDragLeave:
      context.movableRows && !useClientSide
        ? (event) => {
            // Clear drop indicator when leaving grid
            event.api.setRowDropPositionIndicator({
              row: null,
              dropIndicatorPosition: null
            })
          }
        : undefined,
    onSelectionChanged: handlers.onSelectionChanged,
    // SSRM first-page preload: the gridReady seed binds row data into the cache,
    // but AG Grid first renders the seeded block as empty loading-cells and won't
    // recreate them when the data lands — only a forced row redraw (or the server
    // build round-trip's set-columns) does. Fire it ONCE, off the model update
    // that brings the seeded rows into the viewport with data — earlier hooks
    // (onFirstDataRendered/RAF) run before the block is displayed, so they miss.
    onModelUpdated: (event) => {
      revealInitialGrid(event.api)

      if (seedRepainted || !isSeededPreload) {
        return
      }

      const first = event.api.getDisplayedRowAtIndex(0)

      if (first?.data) {
        seedRepainted = true
        event.api.redrawRows()
      }
    },
    onFirstDataRendered: () => {
      // Timing: compute createGrid → firstDataRendered duration and hand it
      // to the dev-gated logger and the reporting seam. Only fires when
      // renderStartRef was populated (stamped just before createGrid).
      if (renderStartRef && renderStartRef.value > 0) {
        const durationMs = measureGridRender(
          renderStartRef.value,
          performance.now()
        )

        const renderContext: GridRenderContext = {
          component:
            (hook.el as HTMLElement).dataset?.component ?? hook.el.tagName,
          columnCount: countLeafColumns(columns),
          rowModelType: useClientSide ? 'clientSide' : 'serverSide'
        }

        logGridRenderTiming(durationMs, renderContext)
        reportGridRender(durationMs, renderContext)
      }
    },
    onGridReady: (event) => {
      hook._gridSorting?.accept(event.api, context.config.sorting ?? [])
      // Preload the first SSRM block from server-inlined page-1 data so block 0
      // paints with no round-trip. Opt-in and safe: only fires for server-side
      // grids where the server passed BOTH `data` and `meta`. Seeded here on
      // gridReady — after columns/cell renderers are live, before the block-0
      // getRows — via the official applyServerSideRowData API. The matching
      // row redraw in onModelUpdated fills the seeded loading cells.
      if (
        !useClientSide &&
        context.config.data?.length &&
        context.config.meta
      ) {
        seedServerSideFirstPage(
          event.api,
          context.config.data as readonly Record<string, unknown>[],
          context.config.meta
        )
      }

      handlers.onGridReady()
    },
    serverSideDatasource: useClientSide ? undefined : serverSideDatasource
  }
}

/** Main facade grid hook implementation */
export const FacadeGrid = {
  /** Initialize the grid when mounted with comprehensive configuration */
  mounted(this: GridViewHook): void {
    // Extract grid configuration using pure functions (lightweight, no DOM)
    const context = createGridContext(this.el as GridHTMLElement)

    // Store grid context BEFORE creating event handlers to prevent race conditions
    this._gridContext = {
      movableRows: context.movableRows,
      readonly: context.readonly
    }
    const runtime = createGridRuntime()
    this._gridRuntime = runtime
    this._gridSorting = context.config.sortingTarget
      ? createGridSorting(
          this,
          context.config.sortingTarget,
          context.config.sorting ?? [],
          {
            invalidateCache: () => this._invalidateDatasourceCache?.(),
            retireCache: () => this._retireDatasourceCache?.(),
            resumeRequests: () => this._resumeDatasourceRequests?.()
          }
        )
      : undefined
    const processGridColumns = createGridColumnProcessor(
      context.config.masterDetail?.masterColumn,
      Boolean(context.config.sortingTarget)
    )

    // Register Phoenix event handlers BEFORE grid creation
    const phoenixHandlers = createPhoenixEventHandlers(
      this,
      processGridColumns,
      runtime
    )
    registerPhoenixHandlers(this, phoenixHandlers)

    const finishInitialization = beginGridInitialization(this.el)
    this._finishInitialization = finishInitialization

    // Give the browser a frame to paint the placeholder before grid construction.
    this._initFrame = requestAnimationFrame(() => {
      this._initFrame = undefined

      try {
        const tooltipConfig = createTooltipConfig()
        const exportConfig = createExportConfig()
        const clipboardConfig = createClipboardConfig()

        const columns = processGridColumns(
          context.config.columns ?? [],
          context.movableRows,
          context.readonly
        )
        const pinnedBottomRowData = pinnedBottomData(
          context.config.columns ?? []
        )

        // Create event handlers with context already available
        const onColumnUpdate = createColumnUpdateHandler(
          context.events.onColumnsUpdate ?? '',
          this,
          context.target
        )
        const onCellValueChanged = createCellValueChangedHandler(
          this,
          runtime,
          context.target,
          context.events.onEdited
        )
        this._cellValueChangedCleanup = onCellValueChanged.cleanup
        const onRowDragEnd = context.movableRows
          ? createRowDragEndHandler(
              this,
              context.target,
              context.events.onRowMoved
            )
          : undefined

        // Create server-side datasource if async mode is enabled with valid event
        // Pass initial data if available for instant first render without server roundtrip
        // Legacy block-0 getRows cache: only when the server inlined `data`
        // WITHOUT `meta`. When `meta` is present the first block is preloaded via
        // applyServerSideRowData on gridReady instead (see buildGridOptions
        // onGridReady), so seeding the getRows cache too would apply block 0 twice.
        const initialData =
          context.config.data?.length && !context.config.meta
            ? {
                data: context.config.data as readonly Record<string, unknown>[]
              }
            : undefined

        const datasourceResult = isValidProgressiveLoadEvent(
          context.events.onProgressiveLoad
        )
          ? createServerSideDatasource(
              context.id,
              context.events.onProgressiveLoad,
              this,
              context.target,
              context.paginationSize,
              initialData,
              this._gridSorting?.canLoad
            )
          : undefined

        // Let Phoenix handlers seed page one after replacing the native cache,
        // before AG Grid's queued loader requests its first block.
        this._resetDatasourceCache = datasourceResult?.resetCache
        this._invalidateDatasourceCache = datasourceResult?.invalidateCache
        this._retireDatasourceCache = datasourceResult?.retireCache
        this._resumeDatasourceRequests = datasourceResult?.resumeRequests
        this._datasourceCleanup = datasourceResult?.cleanup

        warnIfInvalidAsyncConfig(
          context.isAsync,
          Boolean(datasourceResult),
          context.id
        )

        // Mutable ref shared between buildGridOptions (onFirstDataRendered reads it)
        // and the createGrid call site (where the value is stamped).
        const renderStartRef = { value: 0 }

        // Build comprehensive grid options
        const gridOptions = buildGridOptions(
          context,
          columns,
          pinnedBottomRowData,
          gridTheme,
          tooltipConfig,
          exportConfig,
          clipboardConfig,
          {
            onColumnUpdate,
            onCellValueChanged,
            onRowDragEnd,
            onSelectionChanged: context.selectableRows
              ? createSelectionChangedHandler(this, context.id, context.target)
              : undefined,
            onGridReady: createGridReadyHandler(
              this,
              context.id,
              context.events.onBuild,
              context.target
            )
          },
          this,
          runtime,
          datasourceResult?.datasource,
          renderStartRef
        )

        // Capture the createGrid start timestamp for the timing seam.
        // Set immediately before createGrid so the measured duration reflects
        // only the grid initialisation cost, not preceding setup work.
        renderStartRef.value = performance.now()

        // Create the grid with comprehensive configuration
        this.grid = createGrid(this.el, gridOptions)

        // Configure readonly mode after grid creation
        configureGridReadonly(this.grid, context.readonly)
      } catch (error) {
        this._datasourceCleanup?.()
        this._gridSorting?.cleanup()
        this._cellValueChangedCleanup?.()
        runtime.cleanup()
        this.grid?.destroy()
        throw error
      } finally {
        finishInitialization()
        this._finishInitialization = undefined
      }
    })
  },

  /** Retire pending sorting intent while the LiveView connection is unavailable. */
  disconnected(this: GridViewHook): void {
    this._gridSorting?.disconnected()
  },

  /** Restore accepted sorting and retry blocked native loads after reconnecting. */
  reconnected(this: GridViewHook): void {
    this._gridSorting?.reconnected(this.grid)
  },

  /** Cleanup when the grid is destroyed */
  destroyed(this: GridViewHook): void {
    // Cancel deferred init if grid hasn't been created yet
    if (this._initFrame) {
      cancelAnimationFrame(this._initFrame)
      this._initFrame = undefined
    }

    this._finishInitialization?.()
    this._finishInitialization = undefined
    this._datasourceCleanup?.()
    this._gridSorting?.cleanup()
    this._gridSorting = undefined
    this._datasourceCleanup = undefined
    this._resetDatasourceCache = undefined
    this._invalidateDatasourceCache = undefined
    this._retireDatasourceCache = undefined
    this._resumeDatasourceRequests = undefined

    // Clean up grid context reference properly
    this._gridContext = undefined

    this._cellValueChangedCleanup?.()
    this._cellValueChangedCleanup = undefined
    this._gridRuntime?.cleanup()
    this._gridRuntime = undefined

    // Destroy AG Grid instance
    if (this.grid) {
      destroyGridAfterRemoval(this.el, this.grid)
    }
  }
}
